/**
 * NDT inspection reports (fieldiq-api#1152): status skin, kind words, the
 * period line, the PDF filename, who may press what, and every refusal as a
 * sentence.
 *
 * NOTHING HERE COMPUTES A REPORT FIGURE. Coverage, stats, the repair rate and
 * the header are the API's — live on a draft, frozen on an issued report. The
 * AI summary DRAFT is the API's too and is never presented as approved: only
 * `summaryFinal` (a person's text) is "the summary", and only it is printed.
 */
import type { NdtReport, NdtReportStatus } from '../types/ndt-inspection';
import { formatCalendarDate } from './calendar-date';
import { NDT_UNKNOWN_SKIN, canDispatchNdt, type NdtSkin } from './ndt-inspection';

/**
 * Report status. Draft blue (open, nothing printed as final), issued green
 * (numbered, frozen, sent), void slate (out of the record, kept for audit).
 */
export const NDT_REPORT_STATUS_SKIN: Record<NdtReportStatus, NdtSkin> = {
  draft: { label: 'Draft', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  issued: { label: 'Issued', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  void: { label: 'Void', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
};
export const ndtReportStatusSkin = (v: string | null | undefined): NdtSkin =>
  v && Object.prototype.hasOwnProperty.call(NDT_REPORT_STATUS_SKIN, v) ? NDT_REPORT_STATUS_SKIN[v as NdtReportStatus] : NDT_UNKNOWN_SKIN;

export const NDT_REPORT_KIND_LABEL: Record<string, string> = {
  final: 'Final',
  interim: 'Interim',
  daily: 'Daily',
};
/** The API's own kindLabel first; an unknown kind prints `—`, never a guess. */
export const ndtReportKindLabel = (r: Pick<NdtReport, 'kind'> & Partial<Pick<NdtReport, 'kindLabel'>>): string =>
  r.kindLabel || (r.kind && Object.prototype.hasOwnProperty.call(NDT_REPORT_KIND_LABEL, r.kind) ? `${NDT_REPORT_KIND_LABEL[r.kind]} report` : '—');

/** "NDR-0001", or the words a draft gets instead of a number. Void-before-issue has none. */
export function ndtReportNumberText(r: Pick<NdtReport, 'reportNumber' | 'status'>): string {
  if (r.reportNumber) return r.reportNumber;
  if (r.status === 'draft') return 'Draft — numbered at issue';
  return 'Not numbered';
}

/** What the report covers: the whole job, one day, or a range. */
export function ndtReportPeriod(r: Pick<NdtReport, 'kind' | 'periodFrom' | 'periodTo'>): string {
  if (r.kind === 'final') return 'Whole job';
  const from = formatCalendarDate(r.periodFrom) ?? '—';
  if (r.kind === 'daily' || !r.periodTo || r.periodTo === r.periodFrom) return from;
  return `${from} – ${formatCalendarDate(r.periodTo) ?? '—'}`;
}

/**
 * The file a Download saves — the API's own naming (GET /reports/:id/pdf's
 * Content-Disposition): NDR-0001.pdf issued, NDR-0001-VOID.pdf void, and
 * NDJ-0001-DRAFT.pdf for anything never numbered.
 */
export function ndtReportPdfFilename(r: Pick<NdtReport, 'reportNumber' | 'status' | 'jobNumber'>): string {
  if (r.reportNumber) return `${r.reportNumber}${r.status === 'void' ? '-VOID' : ''}.pdf`;
  return `${r.jobNumber || 'report'}-DRAFT.pdf`;
}

/** Every report write is NDT_DISPATCH (admin, office_staff, supervisor, dispatcher). */
export const canWriteNdtReports = canDispatchNdt;

/**
 * EVERY `code` routes/ndt-inspection-reports.routes.ts answers with, as a
 * plain sentence. `ndt-b3.test.tsx` pins the key list, so a code the API adds
 * and this map does not fails a test rather than falling back silently.
 *
 * `invalid_request` is the API's 400 for a bad field; its `error` names the
 * field ("period_to cannot be before period_from"), which is more use than any
 * sentence here, so it prefers the API's text.
 */
export const NDT_REPORT_REFUSAL_MESSAGES: Record<string, string> = {
  job_not_found: 'That job is not on this account.',
  job_cancelled: 'This job is cancelled, so it has no report.',
  job_not_complete:
    'A final report needs the job to be complete. Use an interim or daily report until then.',
  final_exists: 'This job already has a final report.',
  not_draft: 'Only a draft report can be changed. This one is issued or void.',
  no_summary_draft: 'There is no AI draft to approve. Write the summary yourself, or regenerate the draft.',
  already_issued: 'This report is already issued.',
  report_void: 'This report is void. Create a new one instead.',
  no_exams: 'This report covers no interpreted exams, so there is nothing to issue.',
  report_changed: 'The report changed while it was being issued. Try again.',
  report_number_contended: 'Another report was being numbered at the same moment. Try again.',
  already_void: 'This report is already void.',
  report_immutable: 'This report is issued and cannot be changed.',
  signatory_unknown: 'Your user account has no name to sign the report with. Add your name to your profile first.',
  not_on_crew: "You are not on this job's crew, so you cannot see its reports.",
  not_found: 'This report was not found. It may have been deleted.',
  invalid_request: 'The request was not valid.',
};

/** Codes whose API `error` is more specific than the sentence above. */
const PREFER_API_TEXT = new Set(['invalid_request']);

export interface NdtReportRefusal {
  status: number | null;
  code: string | null;
  message: string;
  /** final_exists names the live final — the screen links to it. */
  reportId: string | null;
}

export function ndtReportRefusal(err: unknown, generic: string): NdtReportRefusal {
  const r = (err as { response?: { status?: unknown; data?: Record<string, unknown> } })?.response;
  const d = (r?.data ?? {}) as Record<string, unknown>;
  const status = typeof r?.status === 'number' ? r.status : null;
  const code = typeof d.code === 'string' ? d.code : status === 404 ? 'not_found' : null;
  const apiError = typeof d.error === 'string' && d.error ? d.error : null;
  const known = !!code && Object.prototype.hasOwnProperty.call(NDT_REPORT_REFUSAL_MESSAGES, code);
  const message =
    known && !(PREFER_API_TEXT.has(code as string) && apiError) ? NDT_REPORT_REFUSAL_MESSAGES[code as string] : apiError ?? generic;
  return { status, code, message, reportId: typeof d.reportId === 'string' ? d.reportId : null };
}

/** The issue warnings as words — the API's message (it carries the count), else the code. */
export const NDT_REPORT_WARNING_TEXT: Record<string, string> = {
  summary_not_approved: 'The AI summary draft was not approved, so the report has no Summary section.',
  pending_exams_excluded: 'Some pending exams have no result and are not on the report.',
};
export function ndtReportWarningText(w: { code?: unknown; message?: unknown }): string {
  if (typeof w?.message === 'string' && w.message) return w.message;
  const code = typeof w?.code === 'string' ? w.code : '';
  return Object.prototype.hasOwnProperty.call(NDT_REPORT_WARNING_TEXT, code) ? NDT_REPORT_WARNING_TEXT[code] : code || 'A warning was returned.';
}
